import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, OnInit, signal, ViewChild } from '@angular/core';
import { FormGroupDirective, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';

import { AbstractFormComponent } from '@design-system/forms/abstract-form-component';
import { FormLabelComponent } from '@design-system/typography/form-label/form-label.component';
import { formatNumberForDisplay } from '@utils/number-utils';
export type InputType = 'text' | 'number' | 'text-area' | 'email' | 'password';
export type InputSize = 'default' | 'small';
export type InputTheme = 'default' | 'white';

@Component({
  selector: 'app-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule,
    MatTooltipModule,
    FormLabelComponent
  ]
})
export class InputComponent extends AbstractFormComponent implements OnInit, AfterViewInit {
  protected readonly formatNumberForDisplay = formatNumberForDisplay;

  @Input() type: InputType = 'text';
  @Input() hint = '';
  @Input() minLength:number | null = null;
  @Input() maxLength:number | null = null;
  @Input() minNumericalValue?: number;
  @Input() maxNumericalValue?: number;
  @Input() decimalPlaces = 0;
  @Input() step?: number;
  @Input() trimOnBlur = false;
  @Input() rows = 5;
  @Input() invalidRegexMessage = 'Incorrect format';
  @Input() size: InputSize = 'default';
  @Input() inputTheme: InputTheme = 'default';
  @Input() popoverHelpText?: string;
  @Input() tooltip = '';
  @Input() disabledTooltip = 'This field is disabled.';
  @Input() prefix: string | undefined;
  @Input() showCount = false;

  constructor(   
     protected override elementRef: ElementRef,
     protected override formGroupDirective: FormGroupDirective) 
  {
    super(elementRef, formGroupDirective)
  }

  readonly inputStyle = signal('');

  @ViewChild('prefixSpan') prefixSpan?: ElementRef<HTMLElement>;

  override ngOnInit(): void {
    super.ngOnInit();
    if (this.type !== 'number') {
      if (this.minLength !== null) this.otherValidators.push(Validators.minLength(this.minLength));
      if (this.maxLength !== null) this.otherValidators.push(Validators.maxLength(this.maxLength));
    } else {
      // check if min / max value has been provided, and also check they are valid numbers before applying validators
      if (this.minNumericalValue != null && !isNaN(this.minNumericalValue)) {
        this.otherValidators.push(Validators.min(Number(this.minNumericalValue)));
      }

      if (this.maxNumericalValue != null && !isNaN(this.maxNumericalValue)) {
        this.otherValidators.push(Validators.max(Number(this.maxNumericalValue)));
      }
    }

    this.trySetNumberValue(this.formControl.value);
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.calculateStyle();
  }

  calculateStyle(){
    if(this.prefixSpan?.nativeElement){
      if(this.prefixSpan?.nativeElement?.offsetWidth === 0 && this.prefixSpan?.nativeElement.innerText.length === 1)
        this.inputStyle.set('padding-left:24px');
      else
        this.inputStyle.set('padding-left:' + (this.prefixSpan?.nativeElement?.offsetWidth+16) + 'px');
    }
  }

  onBlur() {
    this.trySetNumberValue((this.formControl.value ?? '').trim());

    if (!this.trimOnBlur) return;

    const value = this.formControl.value as string;
    if (value) this.formControl.setValue(value.trim());
  }

  private trySetNumberValue(value: any) {
    if (this.type !== 'number') return;
    if (value === null || value === undefined || value === '') return;
    if (isNaN(value)) return;
    this.formControl.setValue(Number(value).toFixed(this.decimalPlaces), { emitEvent: false });
  }

  get ngClass(): string[] {
    return [`input-theme-${this.inputTheme}`];
  }

  get characterCount(): number {
    return (this.formControl.value?.length || 0);
  }

  getTooltip(): string {
    if (this.formControl.disabled) {
      return this.disabledTooltip;
    }

    return this.tooltip;
  }

  touched = () => this.formControl.touched;
}
